/**
 * Wording shared by every resync report — the scan panel, the confirm dialog, the write report
 * and both single-column dialogs — so the four surfaces cannot drift apart on how they count,
 * how they name a board, or how they explain a refusal.
 *
 * Pure functions only. Nothing here reads state or a store; every caller hands in what it has.
 */

/**
 * "1 item", "2 items", "1 entry", "3 entries", "1,200 items". Localised here so a count rendered
 * through this helper and one rendered with `toLocaleString()` beside it cannot disagree.
 */
export const plural = (count: number, noun: string, pluralNoun = `${noun}s`): string =>
  `${count.toLocaleString()} ${count === 1 ? noun : pluralNoun}`;

/**
 * "A", "A and B", "A, B and C". No Oxford comma, matching the rest of the product's copy.
 * Empty input gives an empty string so a caller can test for it rather than render "".
 */
export const joinNames = (names: string[]): string => {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

/**
 * A board's name, or an honest fallback when the name cache had forgotten it.
 *
 * "Board 1003" rather than a blank or an invented placeholder: an unnamed board is still a board
 * the user's data is going to, and this feature's reports must never hide one just because it
 * cannot label it prettily. Reachable but rare — names are resolved by the scan job from a
 * cache with a shorter TTL than the job record's.
 */
export const boardLabel = (boardNames: Record<string, string> | undefined, boardId: string): string =>
  boardNames?.[boardId] ?? `Board ${boardId}`;

/**
 * The boards a bulk run touches, as a phrase: "Product Roadmap and Engineering Sprint".
 *
 * Falls back to a count when the summary carries no names at all (a record written before
 * `boardNames` existed), because a sentence listing three "Board 1003"s is worse than "3 boards".
 * A PARTIAL map still lists names, with the fallback label for the gaps — a user who can see two
 * of their three boards named is better served by "Product Roadmap, Engineering Sprint and
 * Board 1003" than by a count that hides the two they recognise.
 */
export const boardsPhrase = (
  boardIds: string[],
  boardNames: Record<string, string> | undefined,
): string => {
  if (boardIds.length === 0) return "no boards";
  if (!boardNames) return plural(boardIds.length, "board");
  return joinNames(boardIds.map((boardId) => boardLabel(boardNames, boardId)));
};

/** "Status" from the engine's `${title}::${type}` column key — the type suffix is never shown. */
export const columnTitleFromKey = (columnKey: string): string => {
  const separator = columnKey.lastIndexOf("::");
  return separator > 0 ? columnKey.slice(0, separator) : columnKey;
};

/** Plain words for a value a report shows. Empty means empty; nothing is rendered blank. */
export const EMPTY_VALUE_LABEL = "Empty";

/**
 * Whether a cell's text counts as empty for display and "no change" purposes. Whitespace-only
 * text is empty: Monday renders it as nothing, and so should a report.
 */
export const isEmptyText = (text: string | null | undefined): boolean =>
  text === null || text === undefined || text.trim() === "";

/**
 * What a failed write means to the user, and what to do about it.
 *
 * The integration service's `reason` strings are accurate and safe to show verbatim (quoted
 * values are already redacted upstream), but they are written from the engine's side of the
 * screen: "status label "Blocked" exists on the target board but is deactivated" is a fact, and
 * "reactivate it on that board, then sync again" is the sentence a user actually needs.
 *
 * Returns the reason verbatim when it matches nothing here, so a new engine message is never
 * hidden behind a generic "something went wrong" — the same rule the subitem dialog has always
 * applied to platform-limit refusals.
 */
export interface FailureExplanation {
  /** What did not happen, in the user's words. */
  message: string;
  /** What they can do about it, when there is something. */
  nextStep?: string;
}

/**
 * Matched on the UNQUOTED words only: the engine redacts every quoted literal in a reason to
 * `"[redacted]"` before it leaves the service (redact.ts), so a matcher that expected the
 * label's text would never fire. Each rewrite keeps the fact the original carried — which
 * column type, which kind of entity, where — and adds only the sentence a user needs next.
 */
const FAILURE_EXPLANATIONS: Array<{
  matches: (reason: string) => boolean;
  explain: (reason: string) => FailureExplanation;
}> = [
  {
    matches: (reason) => /^(status|dropdown) label .* exists on the target board but is deactivated$/.test(reason),
    explain: (reason) => ({
      message: `The ${reason.startsWith("status") ? "status" : "dropdown"} label this value uses exists on that board but is deactivated there, so Monday would not accept it.`,
      nextStep: "Reactivate the label on that board, then sync again.",
    }),
  },
  {
    matches: (reason) => reason === "no synced subitem on this board",
    explain: () => ({ message: "This subitem has no synced copy on that board, so there was nothing to update." }),
  },
  {
    matches: (reason) => reason.startsWith("no synced parent item on this board"),
    explain: (reason) => ({
      message: reason.endsWith("parent board")
        ? "The item this subitem belongs to has no synced copy on that board's parent board, so there was nothing to update."
        : "This item has no synced copy on that board, so there was nothing to update.",
    }),
  },
  {
    matches: (reason) => / columns cannot be resynced \(Monday does not expose a usable value for this type\)$/.test(reason),
    explain: (reason) => ({
      message: `Monday doesn't expose a usable value for ${reason.split(" columns")[0]} columns, so SIMB can't copy it across. This is a Monday platform limitation, not a failure.`,
      nextStep: "Update this column directly on each board.",
    }),
  },
  {
    matches: (reason) => / columns are calculated or system-owned, so their values are not synced between boards$/.test(reason),
    explain: (reason) => ({
      message: `${reason.split(" columns")[0]} columns are calculated or system-owned, so their values are not synced between boards.`,
    }),
  },
  {
    // Monday's verbatim refusal when the VIEWER's token may read a board but not write to it. The
    // resync writes with the viewer's own access, so this is about their permissions on that one
    // board, not about SIMB — and the remedy is a person with edit rights, not a retry.
    matches: (reason) => /^user unauthorized to perform action/i.test(reason.trim()),
    explain: () => ({
      message: "Your Monday account can't edit that board, so SIMB couldn't write there with your access.",
      nextStep: "Ask a board owner or admin to run this sync, or get edit access on that board.",
    }),
  },
  {
    matches: (reason) => reason === "source board holds no value for this column",
    explain: () => ({ message: "The board with the latest change holds no value for this column, so there was nothing to copy." }),
  },
];

export const explainFailure = (reason: string): FailureExplanation => {
  const known = FAILURE_EXPLANATIONS.find((entry) => entry.matches(reason));
  return known ? known.explain(reason) : { message: reason };
};

/**
 * "just now", "1 minute ago", "12 minutes ago", "about an hour ago", "3 hours ago".
 *
 * Minutes, never seconds: the shortest thing this feature times is a chunk of 25 parents, and a
 * count that ticks every second on a 20-minute wait reads as nervous rather than informative.
 * `nowMs` is a parameter so a test can pin the wording without faking the clock.
 */
export const describeElapsed = (sinceMs: number, nowMs = Date.now()): string => {
  const minutes = Math.max(0, Math.round((nowMs - sinceMs) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${plural(minutes, "minute")} ago`;
  const hours = Math.round(minutes / 60);
  return hours === 1 ? "about an hour ago" : `${plural(hours, "hour")} ago`;
};

/**
 * How long the scan has to run before an estimate is offered at all. The first chunk's timing
 * carries the parent enumeration and none of the pacing that governs the rest, so a number read
 * off it is wrong in the direction that is remembered: too optimistic.
 */
export const REMAINING_ESTIMATE_WARMUP_MS = 60_000;

/**
 * "about 15 minutes", "less than a minute", or null when there is nothing to extrapolate from.
 *
 * A straight-line estimate from the parents checked so far against the time that took. Honest
 * about its limits: the scan paces itself against Monday's complexity budget, so the rate can
 * halve mid-run, which is why the wording says "about" and the panel keeps the count beside it.
 * Three things keep the number from twitching, in order of importance:
 *  - the caller must pass a CURRENT `nowMs` — an estimate is elapsed time over work done, and a
 *    stale clock against fresh progress produces a countdown that slides and resets;
 *  - nothing is offered inside the warm-up window, see REMAINING_ESTIMATE_WARMUP_MS;
 *  - past ten minutes the answer is rounded to the nearest five, because "about 23 minutes"
 *    claims a precision the pacing cannot deliver and changes on every poll.
 */
export const describeRemaining = (
  progress: { parentsChecked: number; parentsTotal: number } | undefined,
  startedAtMs: number | undefined,
  nowMs = Date.now(),
): string | null => {
  if (!progress || !startedAtMs) return null;
  if (progress.parentsChecked <= 0 || progress.parentsTotal <= 0) return null;
  const elapsed = nowMs - startedAtMs;
  if (elapsed < REMAINING_ESTIMATE_WARMUP_MS) return null;
  const remainingParents = Math.max(0, progress.parentsTotal - progress.parentsChecked);
  const remainingMs = (elapsed / progress.parentsChecked) * remainingParents;
  if (remainingMs < 60_000) return "less than a minute";
  const minutes = Math.ceil(remainingMs / 60_000);
  const rounded = minutes > 10 ? Math.max(15, Math.round(minutes / 5) * 5) : minutes;
  return `about ${plural(rounded, "minute")}`;
};
